import * as tsx from 'vue-tsx-support';
import { ChevronDown, ChevronUp, StudentView } from '@codegrade/icons';
import { Maybe } from '@codegrade/maybe';
import { expr, maybe } from '@codegrade/utils';
import Collapse from '@/components/Collapse';
import { Icon } from '@/components/Icon';
// @ts-expect-error This component is not yet typed
import SubmitButton from '@/components/SubmitButton';
import * as models from '@/models';
import * as permissions from '@/permissions';
import { CourseRolesStore, CoursesStore, UserStore } from '@/store';
import * as utils from '@/utils';
export default tsx.component({
    name: 'student-view-footer',
    /** @returns The data for this component. */
    data() {
        return {
            missingPermissions: Maybe.empty(),
            hideFooter: false,
        };
    },
    computed: {
        /** @returns What the current state of student view is. */
        studentViewState() {
            return UserStore.studentView();
        },
        /** @returns A maybe of a StudentViewPage object. */
        studentViewPage() {
            const meta = this.$route.meta;
            return meta.getStudentViewPage(this.$route);
        },
        /** @returns The leave student view button if we can actually leave. */
        leaveStudentViewButton() {
            const state = this.studentViewState;
            return utils.ifOrEmpty(state.tag === 'enabled' && state.canLeave, () => (<SubmitButton class="shrink-0" submit={async () => ({
                    onAfterSuccess: async () => await UserStore.popRestrictions(),
                })} successAlert={'You have left the student view'} duration={400}>
                    <span class="text-nowrap">Leave Student View</span>
                </SubmitButton>));
        },
        /** @returns The node of the missing permissions. */
        missingPermissionsNode() {
            const list = this.missingPermissions.extract();
            if (list == null || list.length === 0) {
                return utils.emptyVNode();
            }
            return (<details class="small my-1">
                    <summary>Some student features are not available to you</summary>
                    Your role lacks of the following permissions a student role has:
                    <ul class="my-1 pl-3">
                        {list.map(name => (<li key={name}>{name}</li>))}
                    </ul>
                    You might see a slightly different view than a real student would see.
                </details>);
        },
        /** @returns The body node of the footer. */
        contentNode() {
            return (<div class="px-2 py-1 flex items-center gap-2 student-view-footer-content">
                    <Icon class="size-8 mr-1" icon={StudentView}/>
                    <div class="flex-grow">
                        <div class="font-bold">Student View Enabled</div>
                        <div class="small font-italic">
                            You are now viewing this course as a student. This allows you to inspect
                            and interact with the assignments as a student would.
                        </div>
                        {this.missingPermissionsNode}
                    </div>

                    {this.leaveStudentViewButton}
                </div>);
        },
        /** @returns The rendered body if we need to render it. */
        bodyNode() {
            return expr.match(this.studentViewState, 'tag', {
                disabled: utils.emptyVNode,
                enabled: () => (<div class={['session-footer student-view-footer border rounded-top']}>
                        <button class="mx-2 px-2 py-1 handle d-inline-block border border-bottom-0 rounded-top" v-cg-toggle="student-view-footer-content-collapse" aria-label={`${this.hideFooter ? 'Show' : 'Hide'} the student view footer`}>
                            <Icon icon={this.hideFooter ? ChevronUp : ChevronDown}/>
                        </button>
                        <Collapse id="student-view-footer-content-collapse" v-model={this.hideFooter}>
                            {this.contentNode}
                        </Collapse>
                    </div>),
            });
        },
    },
    /** Called when the component is created. */
    created() {
        utils.watchedLoad({
            instance: this,
            /** @returns The values to load course and course roles with. */
            getValues: () => ({
                tag: this.studentViewState.tag,
                courseId: this.studentViewPage.map(view => view.courseId).extractNullable(),
            }),
            /**
             * Load the missing permissions.
             *
             * @returns The missing permissions.
             */
            async load({ tag, courseId }) {
                if (tag === 'disabled' || courseId == null) {
                    return Maybe.empty();
                }
                await Promise.all([
                    CoursesStore.loadSingleCourse({ courseId }),
                    CourseRolesStore.loadStudentRole({ courseId }),
                ]);
                return maybe
                    .and(CoursesStore.getCourse()(courseId).filter(course => course instanceof models.Course), CourseRolesStore.getStudentRole()(courseId)
                    .join()
                    .map(r => r.perms))
                    .map(([course, permMap]) => permissions.allCoursePermissions.filter(key => permMap[key] &&
                    !course.hasPermission(permissions.CoursePermissions[key])));
            },
            reset: () => {
                this.missingPermissions = Maybe.empty();
            },
            setError: error => {
                // eslint-disable-next-line no-console
                console.error('Failed to load missing permissions', error);
            },
            setResult: missingPerms => {
                this.missingPermissions = missingPerms;
            },
        });
    },
    /** @returns The rendered component. */
    render() {
        return this.bodyNode;
    },
});
